import { useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AgenciaVagaResumida {
  id: string;
  titulo: string;
  status: string;
  criada_em: string;
  data_aprovacao: string | null;
}

interface VagaLinhaBruta {
  id: string;
  agencia: string | null;
  cliente: string | null;
  status: string;
  created_at: string;
  data_aprovacao: string | null;
}

export const AGENCIA_VAGAS_PAGE_SIZE = 20;

interface AgenciaVagasPagina {
  linhas: AgenciaVagaResumida[];
}

/** Mesmo CASE usado no backend (sql/120, sql/146, sql/150): "agência - cliente" quando
 *  cliente preenchido, senão só o nome da agência gravado na vaga. */
function composeVagaTitulo(agencia: string | null, cliente: string | null): string {
  if (cliente && cliente.trim() !== "") return `${agencia ?? ""} - ${cliente}`;
  return agencia ?? "Vaga sem título";
}

/**
 * TODAS as vagas do contrato com a agência (aba "Vagas" da página de
 * detalhe) — não limitado à janela de tempo da timeline. Consulta direta à
 * view `vagas` (soft-delete aware, sql/120), com a mesma composição de
 * título do backend. Paginação server-side via .range() (páginas de 20,
 * "Mostrar mais" acumulando), mesmo padrão do useVagaColumn.
 */
export function useAgenciaVagas(agenciaId?: string | null) {
  return useInfiniteQuery({
    queryKey: ["agencia-vagas", agenciaId],
    enabled: !!agenciaId,
    staleTime: 5 * 60 * 1000,
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<AgenciaVagasPagina> => {
      const inicio = pageParam;
      const fim = inicio + AGENCIA_VAGAS_PAGE_SIZE - 1;
      // "vagas" as never: agencia_id/data_aprovacao (M19/M89) não estão no
      // types.ts gerado (defasado) — sem o cast, a resolução de overload do
      // PostgrestFilterBuilder trava em "type instantiation excessively deep"
      // (mesmo padrão de EmpresaDetailPage.tsx para tabelas/colunas novas).
      const { data, error } = await supabase
        .from("vagas" as never)
        .select("id, agencia, cliente, status, created_at, data_aprovacao")
        .eq("agencia_id", agenciaId)
        .order("created_at", { ascending: false })
        .range(inicio, fim);
      if (error) throw error;
      const linhas = ((data as unknown as VagaLinhaBruta[]) ?? []).map((linha) => ({
        id: linha.id,
        titulo: composeVagaTitulo(linha.agencia, linha.cliente),
        status: linha.status,
        criada_em: linha.created_at,
        data_aprovacao: linha.data_aprovacao,
      }));
      return { linhas };
    },
    getNextPageParam: (ultimaPagina, todasPaginas) => {
      if (ultimaPagina.linhas.length < AGENCIA_VAGAS_PAGE_SIZE) return undefined;
      return todasPaginas.reduce((total, pagina) => total + pagina.linhas.length, 0);
    },
  });
}
